import React, { useMemo } from "react";
import { TableCell, TableRow, Typography, Chip, Box, Tooltip } from "@mui/material";
import { Iconify } from "../../../Minimal/src/components/iconify/iconify";
import { calculateItemVat, calculateItemSubtotal, parseQty, resolveOrderTypeFromProduct, resolveCartLineDisplayTotal, resolveCartLineInlinePromoGrossDisplayTotal, resolveCartLineStrikethroughPrice, resolveProductTaxCode, shouldShowCartLineInlinePromoDiscount, resolveCartLineInlinePromoDiscountForDisplay, shouldShowCartPromotionSummarySubRow, resolveCartPromotionSummaryBuyDiscountForDisplay, resolveCartPromotionSummarySubRowCount } from "../../Epos/utils/cartCalculations";
import { isCartLineOfferDisplayRole } from "../../Promotion/utils/promotionOfferProductsShared";

const ViewCartTableRowEposSales = ({
  item,
  index,
  showVatAsAmount = false,
  itemDetails = null,
  isTelecaller = false,
  showPromoIndicator = true,
  bundleDiscounts = [],
  cartItems = [],
}) => {
  const code = item.code || item.product_code || "";
  const description = item.desc || item.name || item.product_name || "";
  const promotion_name = item.promotion_name || item.promotionName || item.promo_name || "";
  const orderType = resolveOrderTypeFromProduct(item);
  const qty = (item.order_qty !== undefined && item.order_qty !== null && item.order_qty !== "")
    ? parseInt(item.order_qty) || 0
    : parseInt(item.qty) || 0;
  const currentPrice = parseFloat(item.price) || 0;
  const discount = (() => {
    const overrideDiscount = item.manualOverrides?.discount;
    const apiItemDiscount = item.item_discount !== undefined && item.item_discount !== null && item.item_discount !== ""
      ? parseFloat(item.item_discount)
      : null;
    const itemDiscount = item.discount || item.productWiseDiscountValue;
    const rawValue = parseFloat(
      overrideDiscount !== undefined && overrideDiscount !== null
        ? overrideDiscount
        : (apiItemDiscount !== null && Number.isFinite(apiItemDiscount)
          ? apiItemDiscount
          : (itemDiscount !== undefined && itemDiscount !== null ? itemDiscount : 0))
    );

    if (!Number.isFinite(rawValue) || rawValue < 0) {
      return 0;
    }

    return rawValue;
  })();
  const rawVatRate = parseFloat(item.tax_rate ?? item.rate ?? "");
  const vatRate = Number.isFinite(rawVatRate) && rawVatRate >= 0 ? rawVatRate : 0;
  const isContractPrice = item.isContractPriceApplied === "Y";

  const originalPrice = (() => {
    if (item.useBackendCalculations) {
      if (item.is_promoted === "1" && item.original_price !== undefined && item.original_price > 0) {
        return parseFloat(item.original_price);
      }
      if (item.base_unit_price !== undefined && item.base_unit_price > 0) {
        return parseFloat(item.base_unit_price);
      }
    }
    return orderType === "Box"
      ? parseFloat(item.sales_box_price || 0)
      : parseFloat(item.sales_piece_price || 0);
  })();

  const itemDiscountType = item.discount_type || item.manualOverrides?.discount_type || item.productWiseDiscountType || "amount";
  const normalizedDiscountType = String(itemDiscountType).trim().toLowerCase();
  const isFixedDiscount = normalizedDiscountType === "fixed" || normalizedDiscountType === "£" || normalizedDiscountType === "amount";

  const actualPrice = item.useBackendCalculations && item.unit_price !== undefined
    ? parseFloat(item.unit_price)
    : currentPrice;
  const discountPercentage = parseFloat(item.discount_percentage || 0);
  const hasPriceDifference = originalPrice > 0 && Math.abs(originalPrice - actualPrice) > 0.01;
  const shouldShowOriginalPrice = (discount > 0 || discountPercentage > 0 || hasPriceDifference) && originalPrice > 0 && hasPriceDifference;

  const isFree = itemDetails?.isFree || false;
  const isGetPromotion = itemDetails?.isGetPromotion || false;
  const normalizedPromoType = (() => {
    let t = String(item.promo_type ?? item.promoType ?? item.promosType ?? "").trim().toUpperCase();
    if (!t && item.buyOrGet) {
      const b = String(item.buyOrGet).trim().toUpperCase();
      if (b === "BUY" || b === "GET" || b === "FREE") t = b;
    }
    return t;
  })();
  const isGetPromoType = normalizedPromoType === "GET";
  const isPromoTypeFree = normalizedPromoType === "FREE";
  const isOfferLineDisplay = isCartLineOfferDisplayRole(item);
  const isFreeItem = isOfferLineDisplay && !isGetPromoType;
  const isPromoFreeOrGetProductName = isOfferLineDisplay;
  const isItemPromoted =
    item.is_promoted === "1" ||
    item.is_promoted === 1 ||
    !!(item.promo_id || item.promoId || item.promosId || item.promotion_id || item.promotionId);
  const promotionApplied =
    item.promotion_applied === true ||
    item.promotion_applied === "true" ||
    item.promotion_applied === 1 ||
    item.promotion_applied === "1";
  const promotionIdRaw = item.promotion_id ?? item.promotionId ?? item.promosId ?? item.promoId ?? item.promo_id;
  const hasValidPromotionId =
    promotionIdRaw !== undefined &&
    promotionIdRaw !== null &&
    promotionIdRaw !== "" &&
    Number.isFinite(Number(promotionIdRaw)) &&
    Number(promotionIdRaw) > 0;
  const promotionExplicitlyRemoved =
    item.promotion_applied === false ||
    item.promotion_applied === "false" ||
    item.promotion_applied === 0 ||
    item.promotion_applied === "0";
  const hasPromoDiscountAmount =
    parseFloat(item.promo_disc_percentage ?? 0) > 0 ||
    parseFloat(item.promo_disc_amount ?? 0) > 0;
  const hasManualDiscountForPromoIndicator =
    (item.manualOverrides?.discount !== undefined &&
      item.manualOverrides?.discount !== null &&
      parseFloat(String(item.manualOverrides.discount || 0)) > 0) ||
    (!promotionApplied && discount > 0 && !hasPromoDiscountAmount);
  const hasStalePromoMetadataShape =
    isItemPromoted ||
    hasValidPromotionId ||
    normalizedPromoType === "BUY" ||
    normalizedPromoType === "GET";
  const suppressPromoIndicator =
    promotionExplicitlyRemoved ||
    (!promotionApplied &&
      !hasPromoDiscountAmount &&
      hasManualDiscountForPromoIndicator &&
      hasStalePromoMetadataShape);
  const baseShowPromoIndicator =
    !suppressPromoIndicator &&
    (promotionApplied || hasValidPromotionId || isItemPromoted);
  const shouldShowPromoIndicator = showPromoIndicator && baseShowPromoIndicator;
  const promotionNameTrimmed = String(promotion_name || "").trim();
  const showPromotionNameLabel =
    shouldShowPromoIndicator && !!promotionNameTrimmed && item.showPromo === true;
  const promoSummaryOptions = { cartItems };
  const showPromotionSummarySubRow = shouldShowCartPromotionSummarySubRow(
    item,
    bundleDiscounts,
    promoSummaryOptions
  );
  const promotionSummaryBuyDiscountNet = resolveCartPromotionSummaryBuyDiscountForDisplay(
    item,
    bundleDiscounts,
    promoSummaryOptions
  );
  const promotionSummarySubRowCount = showPromotionSummarySubRow
    ? resolveCartPromotionSummarySubRowCount(item, cartItems)
    : 0;
  const promotionSummaryUnitDiscount =
    promotionSummarySubRowCount > 1
      ? promotionSummaryBuyDiscountNet / promotionSummarySubRowCount
      : promotionSummaryBuyDiscountNet;
  const inlinePromoDiscountNet = resolveCartLineInlinePromoDiscountForDisplay(item);
  const showInlinePromoDiscount = shouldShowCartLineInlinePromoDiscount(item, cartItems);
  const showPromotionNameInlineUnderDescription =
    showPromotionNameLabel && !showPromotionSummarySubRow;
  const isPromotionApplied = item.is_promoted === 0;
  const promoIndicatorLetter =
    normalizedPromoType === "FREE" && shouldShowPromoIndicator
      ? "F"
      : (normalizedPromoType === "GET" || normalizedPromoType === "BUY") && shouldShowPromoIndicator
        ? "P"
        : isItemPromoted && shouldShowPromoIndicator && isPromotionApplied
          ? "P"
          : null;

  const checkEditModeDiscounted = () => {
    const hasManualDiscount = item.manualOverrides?.discount !== undefined && item.manualOverrides?.discount !== null && parseFloat(item.manualOverrides.discount || 0) > 0;
    
    if (hasManualDiscount) {
      return false;
    }

    if (item.promotion_applied === false || item.promotion_applied === "false") {
      return false;
    }

    const promotionApplied = item.promotion_applied === true || item.promotion_applied === "true";
    const promotionId = item.promotion_id !== undefined && item.promotion_id !== null && item.promotion_id !== "";
    const hasPromotionApplied = promotionApplied || promotionId;

    if (!hasPromotionApplied) {
      return false;
    }

    const promoDiscPct = parseFloat(item.promo_disc_percentage ?? 0);
    const promoDiscAmt = parseFloat(item.promo_disc_amount ?? 0);
    if (promoDiscPct === 0 && promoDiscAmt === 0) {
      return false;
    }

    const currentDiscount = parseFloat(item.discount || 0);
    const hasLineDiscount = item.line_discount !== undefined && item.line_discount !== null && parseFloat(item.line_discount || 0) > 0;
    const hasDiscountPercentage = item.discount_percentage !== undefined && item.discount_percentage !== null && parseFloat(item.discount_percentage || 0) > 0;

    if (currentDiscount > 0 && !hasLineDiscount && !hasDiscountPercentage) {
      return false;
    }

    const promoUnitPrice = item.promo_unit_price !== undefined && item.promo_unit_price !== null
      ? parseFloat(item.promo_unit_price)
      : null;
    const unitPrice = item.unit_price !== undefined && item.unit_price !== null
      ? parseFloat(item.unit_price)
      : null;
    const baseUnitPrice = item.base_unit_price !== undefined && item.base_unit_price !== null
      ? parseFloat(item.base_unit_price)
      : null;

    if (promoUnitPrice !== null && promoUnitPrice > 0) {
      const referencePrice = unitPrice !== null ? unitPrice : (baseUnitPrice !== null ? baseUnitPrice : null);
      if (referencePrice !== null && referencePrice > 0 && Math.abs(referencePrice - promoUnitPrice) > 0.01) {
        return true;
      }
    }

    if (hasLineDiscount || hasDiscountPercentage) {
      return true;
    }

    return false;
  };

  const showOriginalPrice = checkEditModeDiscounted();

  const allotQty = (() => {
    const value = item.allot_qty !== undefined && item.allot_qty !== null && item.allot_qty !== ""
      ? item.allot_qty
      : (item.accept_qty !== undefined && item.accept_qty !== null && item.accept_qty !== ""
        ? item.accept_qty
        : String(qty));
    const numValue = parseInt(value, 10);
    return Number.isFinite(numValue) && numValue >= 0 ? numValue : qty;
  })();

  const updatedItem = {
    ...item,
    qty: qty,
    unit_price: item.unit_price || item.price,
    discount: discount,
    discount_type: itemDiscountType,
    tax_rate: vatRate
  };

  const vatAmount = item.useBackendCalculations && (item.line_vat !== undefined || item.line_tax !== undefined || item.vat !== undefined || item.tax !== undefined)
    ? parseFloat(item.line_vat || item.line_tax || item.vat || item.tax || 0)
    : calculateItemVat(updatedItem);

  const lineQtyForTotal = parseQty(item, allotQty);

  const calculateTotal = () => {
    const netTotal = resolveCartLineDisplayTotal(item, lineQtyForTotal, {
      discount,
      discountType: itemDiscountType,
      isEditMode: true,
    });
    return resolveCartLineInlinePromoGrossDisplayTotal(netTotal);
  };

  const vatPercentDisplay = `${Number.isFinite(vatRate) ? vatRate.toFixed(2) : "0.00"}%`;
  const vatAmountDisplay = `£${vatAmount.toFixed(2)}`;
  const taxCodeDisplay = resolveProductTaxCode(item) || "-";
  const vatDisplayValue = isTelecaller
    ? taxCodeDisplay
    : showVatAsAmount
      ? vatAmountDisplay
      : vatPercentDisplay;

  const discountDisplay = useMemo(() => {
    if (discount === 0) return "0.00";
    if (isFixedDiscount) {
      return `£${discount.toFixed(2)}`;
    } else {
      return `${discount.toFixed(2)}%`;
    }
  }, [discount, isFixedDiscount]);

  const priceDisplay = useMemo(() => {
    const promotionApplied = item.promotion_applied === true || item.promotion_applied === "true";
    const hasPromoPrice = item.promo_unit_price !== undefined && item.promo_unit_price !== null;
    const hasUnitPrice = item.unit_price !== undefined && item.unit_price !== null;
    
    let displayPrice = 0;
    let originalPrice = null;
    let shouldShowStrikethrough = false;
    
    if (item.useBackendCalculations) {
      if (hasPromoPrice) {
        const promoPrice = parseFloat(item.promo_unit_price);
        const strikePrice = resolveCartLineStrikethroughPrice(item);
        displayPrice = promoPrice;

        if (strikePrice > 0.01 && promoPrice < strikePrice - 0.01) {
          originalPrice = strikePrice;
          shouldShowStrikethrough = true;
        }
      } else if (hasUnitPrice) {
        displayPrice = parseFloat(item.unit_price);
      }
    } else {
      displayPrice = actualPrice;
    }

    return { displayPrice, originalPrice, shouldShowStrikethrough };
  }, [item, actualPrice]);

  return (
    <>
    <TableRow hover
      sx={{ '& .MuiTableCell-root': { p: 0.7 } }}
    >
      <TableCell sx={{ fontSize: '0.7rem' }} align="center">
        <Typography className="cartData">
          {index + 1}
        </Typography>
      </TableCell>
      <TableCell sx={{ fontSize: '0.7rem' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography className="cartData">
            {code}
          </Typography>
          {promoIndicatorLetter === "F" && (
            <Typography
              variant="caption"
              fontWeight={500}
              color="info.darker"
              sx={{
                fontSize: '0.65rem',
                display: "flex",
                alignItems: "center",
                gap: 0.5
              }}
            >
              <Iconify icon="solar:check-circle-bold" width={12} />
              (F)
            </Typography>
          )}
          {promoIndicatorLetter === "P" && (
            <Typography
              variant="caption"
              fontWeight={500}
              color="info.darker"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                fontSize: '0.65rem',
              }}
            >
              <Iconify icon="solar:check-circle-bold" width={12} />
              (P)
            </Typography>
          )}
        </Box>
      </TableCell>
      <TableCell sx={{ fontSize: '0.7rem', verticalAlign: 'middle' }}>
        <Typography className="cartData">
          {orderType}
        </Typography>
      </TableCell>
      <TableCell sx={{ maxWidth: 200 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              className="cartData"
              sx={{
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                ...(isPromoFreeOrGetProductName ? { color: '#0456f1' } : {}),
              }}
              title={description}
            >
              {description}
            </Typography>
            {isContractPrice && (
              <Chip
                label="C"
                size="small"
                color="primary"
                sx={{ height: 16, fontSize: '0.65rem', fontWeight: 600 }}
              />
            )}
          </Box>
          {showPromotionNameInlineUnderDescription ? (
            <Typography
              variant="caption"
              color="#0456f1"
              sx={{
                fontSize: '0.65rem',
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textTransform: 'uppercase',
              }}
              title={promotionNameTrimmed}
            >
              {promotionNameTrimmed}
            </Typography>
          ) : null}
        </Box>
      </TableCell>
      <TableCell align="center">
        <Typography className="cartData">
          {allotQty}
        </Typography>
      </TableCell>
      {isTelecaller ? (
        <>
          <TableCell align="right">
            {(() => {
              const orderType = item.order_type || item.type || "Box";
              const stdPriceRaw = item.original_price ?? (orderType === "Box" ? item.sales_box_price : item.sales_piece_price) ?? item.standard_price ?? 0;
              const stdPrice = Number.isFinite(parseFloat(stdPriceRaw)) && parseFloat(stdPriceRaw) >= 0 ? parseFloat(stdPriceRaw) : 0;
              
              return (
                <Typography className="cartData">
                  £{stdPrice.toFixed(2)}
                </Typography>
              );
            })()}
          </TableCell>
          <TableCell align="right">
            {priceDisplay.shouldShowStrikethrough && priceDisplay.originalPrice !== null ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'right', gap: 0.25 }}>
                <Typography 
                  className="cartData"
                  sx={{ 
                    textDecoration: 'line-through', 
                    opacity: 0.6,
                    fontSize: '0.65rem'
                  }}
                >
                  £{priceDisplay.originalPrice.toFixed(2)}
                </Typography>
                <Typography className="cartData">
                  £{priceDisplay.displayPrice.toFixed(2)}
                </Typography>
              </Box>
            ) : (
              <Typography className="cartData">
                £{priceDisplay.displayPrice.toFixed(2)}
              </Typography>
            )}
          </TableCell>
        </>
      ) : (
        <TableCell align="right">
          {priceDisplay.shouldShowStrikethrough && priceDisplay.originalPrice !== null ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'right', gap: 0.25 }}>
              <Typography 
                className="cartData"
                sx={{ 
                  textDecoration: 'line-through', 
                  opacity: 0.6,
                  fontSize: '0.65rem'
                }}
              >
                £{priceDisplay.originalPrice.toFixed(2)}
              </Typography>
              <Typography variant="caption" color="inherit" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                £{priceDisplay.displayPrice.toFixed(2)}
              </Typography>
            </Box>
          ) : (
            <Typography variant="caption" color="inherit">
              £{priceDisplay.displayPrice.toFixed(2)}
            </Typography>
          )}
        </TableCell>
      )}
      <TableCell sx={{minWidth: 50, whiteSpace: 'nowrap' }} align="right">
        {isTelecaller ? (
          <Tooltip arrow placement="top" title={`VAT: ${vatAmountDisplay} (${vatPercentDisplay})`}>
            <Typography variant="caption" fontSize="0.75rem" fontWeight={600} component="span">
              {vatDisplayValue}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="caption" fontSize="0.75rem" fontWeight={600}>
            {vatDisplayValue}
          </Typography>
        )}
      </TableCell>
      <TableCell align="right">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          <Typography variant="caption" fontSize="0.75rem" fontWeight={600}>
            £{calculateTotal().toFixed(2)}
          </Typography>
          {showInlinePromoDiscount ? (
            <Typography variant="caption" fontSize="0.75rem" fontWeight={600} sx={{ color: '#0456f1' }}>
              -£{inlinePromoDiscountNet.toFixed(2)}
            </Typography>
          ) : null}
        </Box>
      </TableCell>
      <TableCell sx={{ textAlign: 'center'}}>
 
      </TableCell>
    </TableRow>
    {promotionSummarySubRowCount > 0
      ? Array.from({ length: promotionSummarySubRowCount }, (_, promoRowIndex) => (
      <TableRow
        key={`${item.id || index}-promo-summary-${promoRowIndex}`}
        sx={{
          '& .MuiTableCell-root': {
            p: 0.4,
            borderTop: 0,
          },
        }}
      >
        <TableCell />
        <TableCell />
        <TableCell />
        <TableCell sx={{ maxWidth: 200 }}>
          <Typography
            variant="caption"
            color="#0456f1"
            fontWeight={500}
            sx={{
              fontSize: '0.75rem',
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
              textTransform: 'uppercase',
            }}
            title={promotionNameTrimmed}
          >
            {promotionNameTrimmed}
          </Typography>
        </TableCell>
        <TableCell />
        {isTelecaller ? (
          <>
            <TableCell />
            <TableCell />
          </>
        ) : (
          <TableCell />
        )}
        <TableCell />
        <TableCell align="right">
          <Typography variant="caption" fontSize="0.75rem" fontWeight={600} sx={{ color: '#0456f1' }}>
            -£{promotionSummaryUnitDiscount.toFixed(2)}
          </Typography>
        </TableCell>
        <TableCell />
      </TableRow>
    ))
    : null}
    </>
  );
};

export default React.memo(ViewCartTableRowEposSales);
